import { GameVariant } from '../types/enums.js';
import { VARIANT_SPECS, type VariantSpec } from '../generated/variants.gen.js';

/**
 * Configurazione scacchiera per variante
 */
export interface BoardConfig {
  size: number;           // Dimensione griglia (8 o 10)
  playableSquares: number; // Caselle giocabili
  piecesPerPlayer: number; // Pezzi iniziali per giocatore
  initialWhite: number[];  // Caselle iniziali bianco
  initialBlack: number[];  // Caselle iniziali nero
  drawMoveLimit: number;  // Halfmoves per patta (Italian: 80 = 40 mosse, International: 50 = 25 mosse)
  darkSquareBottomRight: boolean; // true = dark square bottom-right (Italian), false = dark square bottom-left (International/English)
  blackStarts?: boolean; // true = nero muove per primo (English draughts)
}

/**
 * International Draughts 10×10
 * Caselle numerate 1-50
 * Bianco: righe 7-10 (caselle 31-50)
 * Nero: righe 1-4 (caselle 1-20)
 */
export const INTERNATIONAL_CONFIG: BoardConfig = {
  size: 10,
  playableSquares: 50,
  piecesPerPlayer: 20,
  drawMoveLimit: 50, // 50 halfmoves = 25 mosse complete di sole dame senza cattura (FMJD Art. 6.2 verified)
  darkSquareBottomRight: false,
  // Nero inizia in alto (caselle 1-20)
  initialBlack: [
    1, 2, 3, 4, 5,
    6, 7, 8, 9, 10,
    11, 12, 13, 14, 15,
    16, 17, 18, 19, 20,
  ],
  // Bianco inizia in basso (caselle 31-50)
  initialWhite: [
    31, 32, 33, 34, 35,
    36, 37, 38, 39, 40,
    41, 42, 43, 44, 45,
    46, 47, 48, 49, 50,
  ],
};

/**
 * Italian Draughts 8×8 (Dama Italiana)
 * Caselle numerate 1-32
 * Nero: righe 1-3 (caselle 1-12)
 * Bianco: righe 6-8 (caselle 21-32)
 *
 * Regole speciali:
 * - Pedine catturano SOLO in avanti
 * - Dame si muovono di 1 sola casella (no flying king)
 * - Priorità cattura: dama obbligatoria, poi max pezzi, poi max dame
 */
export const ITALIAN_CONFIG: BoardConfig = {
  size: 8,
  playableSquares: 32,
  piecesPerPlayer: 12,
  drawMoveLimit: 80, // 40 mosse complete di sole dame senza cattura (FID 2025)
  darkSquareBottomRight: true,
  // Nero inizia in alto (caselle 1-12, righe 1-3)
  initialBlack: [
    1, 2, 3, 4,
    5, 6, 7, 8,
    9, 10, 11, 12,
  ],
  // Bianco inizia in basso (caselle 21-32, righe 6-8)
  initialWhite: [
    21, 22, 23, 24,
    25, 26, 27, 28,
    29, 30, 31, 32,
  ],
};

/**
 * English Draughts (Checkers) 8×8
 * Caselle numerate 1-32, orientamento internazionale (casella scura in basso a sinistra)
 * Nero: righe 1-3 (caselle 1-12, in alto)
 * Bianco: righe 6-8 (caselle 21-32, in basso)
 *
 * Regole speciali:
 * - Pedine catturano solo in avanti
 * - Dame si muovono di 1 sola casella (no flying king)
 * - Cattura obbligatoria ma nessuna priorità (scelta libera)
 */
export const ENGLISH_CONFIG: BoardConfig = {
  size: 8,
  playableSquares: 32,
  piecesPerPlayer: 12,
  drawMoveLimit: 80, // 40 mosse complete di sole dame senza cattura
  darkSquareBottomRight: false,
  blackStarts: true, // Nella dama inglese inizia il nero
  // Nero inizia in alto (caselle 1-12, righe 1-3)
  initialBlack: [
    1, 2, 3, 4,
    5, 6, 7, 8,
    9, 10, 11, 12,
  ],
  // Bianco inizia in basso (caselle 21-32, righe 6-8)
  initialWhite: [
    21, 22, 23, 24,
    25, 26, 27, 28,
    29, 30, 31, 32,
  ],
};

/**
 * Brazilian Draughts 8×8 — regole International su otto caselle per lato.
 *
 * DERIVATA dalla matrice, non scritta a mano. Prima era un letterale che faceva
 * `...INTERNATIONAL_CONFIG` sovrascrivendo tutto TRANNE `drawMoveLimit`, quindi
 * ereditava il 50 dell'International — mentre la matrice diceva 80. Il motore
 * (`determineGameOutcome`, unico lettore a runtime) pattava a 50 semi-mosse e la
 * pagina Regole, che legge la matrice, prometteva 40 mosse complete: 80.
 *
 * Il valore giusto è 50, e la matrice è stata corretta: la Brasiliana è
 * l'International su 8x8 (dama volante, presa all'indietro, `maxPieces`), quindi
 * segue la regola FMJD Art. 6.2 e non l'80 della FID, che vale per Italiana e
 * Inglese. Ma il difetto era la DIVERGENZA, non il numero: derivandola
 * dall'adattatore non può ripresentarsi. Verificato che il config derivato è
 * identico a quello scritto a mano su tutti gli altri sette campi.
 */
export const BRAZILIAN_CONFIG: BoardConfig = specToBoardConfig(
  VARIANT_SPECS[GameVariant.BRAZILIAN],
);

/**
 * Adapter: BoardConfig derivato dalla Master Matrix.
 * Usato per le varianti dichiarate nella matrice ma non ancora attive lato runtime
 * (RUSSIAN, SPANISH, TRIESTINE, TURKISH, FRISIAN, CANADIAN, HEX, ...).
 *
 * Nota: in PR2 anche INTERNATIONAL/ITALIAN/ENGLISH/BRAZILIAN passeranno per questo adapter,
 * eliminando i 4 config hardcoded sopra.
 */
function specToBoardConfig(spec: VariantSpec): BoardConfig {
  const totalSquares = spec.board.size * spec.board.size;
  const playable =
    spec.board.playableSquares === 'all' ? totalSquares : Math.floor(totalSquares / 2);
  return {
    size: spec.board.size,
    playableSquares: playable,
    piecesPerPlayer: spec.board.piecesPerPlayer,
    initialWhite: [...spec.board.initialWhite],
    initialBlack: [...spec.board.initialBlack],
    drawMoveLimit: spec.rules.drawMoveLimit,
    darkSquareBottomRight: spec.ui.darkSquareBottomRight,
    ...(spec.board.blackStarts ? { blackStarts: true } : {}),
  };
}

/**
 * Mappa variante → configurazione
 */
export const VARIANT_CONFIGS: Record<GameVariant, BoardConfig> = {
  [GameVariant.INTERNATIONAL]: INTERNATIONAL_CONFIG,
  [GameVariant.ITALIAN]: ITALIAN_CONFIG,
  [GameVariant.ENGLISH]: ENGLISH_CONFIG,
  [GameVariant.BRAZILIAN]: BRAZILIAN_CONFIG,
  [GameVariant.FRISIAN]: specToBoardConfig(VARIANT_SPECS[GameVariant.FRISIAN]),
  [GameVariant.FRYSK]: specToBoardConfig(VARIANT_SPECS[GameVariant.FRYSK]),
  [GameVariant.TURKISH]: specToBoardConfig(VARIANT_SPECS[GameVariant.TURKISH]),
  [GameVariant.ARMENIAN]: specToBoardConfig(VARIANT_SPECS[GameVariant.ARMENIAN]),
  [GameVariant.RUSSIAN]: specToBoardConfig(VARIANT_SPECS[GameVariant.RUSSIAN]),
  [GameVariant.SPANISH]: specToBoardConfig(VARIANT_SPECS[GameVariant.SPANISH]),
  [GameVariant.TRIESTINE]: specToBoardConfig(VARIANT_SPECS[GameVariant.TRIESTINE]),
  [GameVariant.CANADIAN]: specToBoardConfig(VARIANT_SPECS[GameVariant.CANADIAN]),
  [GameVariant.NIGERIAN]: specToBoardConfig(VARIANT_SPECS[GameVariant.NIGERIAN]),
  [GameVariant.GHANAIAN]: specToBoardConfig(VARIANT_SPECS[GameVariant.GHANAIAN]),
  [GameVariant.HEX]: specToBoardConfig(VARIANT_SPECS[GameVariant.HEX]),
  [GameVariant.POOL]: specToBoardConfig(VARIANT_SPECS[GameVariant.POOL]),
  [GameVariant.CZECH]: specToBoardConfig(VARIANT_SPECS[GameVariant.CZECH]),
  [GameVariant.THAI]: specToBoardConfig(VARIANT_SPECS[GameVariant.THAI]),
  [GameVariant.PORTUGUESE]: specToBoardConfig(VARIANT_SPECS[GameVariant.PORTUGUESE]),
  [GameVariant.FILIPINO]: specToBoardConfig(VARIANT_SPECS[GameVariant.FILIPINO]),
  [GameVariant.MALAYSIAN]: specToBoardConfig(VARIANT_SPECS[GameVariant.MALAYSIAN]),
  [GameVariant.GERMAN]: specToBoardConfig(VARIANT_SPECS[GameVariant.GERMAN]),
  [GameVariant.JAMAICAN]: specToBoardConfig(VARIANT_SPECS[GameVariant.JAMAICAN]),
  [GameVariant.SINHALESE]: specToBoardConfig(VARIANT_SPECS[GameVariant.SINHALESE]),
  [GameVariant.SOUTH_AFRICAN]: specToBoardConfig(VARIANT_SPECS[GameVariant.SOUTH_AFRICAN]),
  [GameVariant.DAMEO]: specToBoardConfig(VARIANT_SPECS[GameVariant.DAMEO]),
  [GameVariant.CRODA]: specToBoardConfig(VARIANT_SPECS[GameVariant.CRODA]),
};

/**
 * Costanti di default (International)
 */
export const BOARD_SIZE = 10;
export const TOTAL_SQUARES = 50;
export const PIECES_PER_PLAYER = 20;

/**
 * Clock low time threshold (ms) — under this, clock shows warning styling
 *
 * @deprecated The threshold now depends on the cadence: use `getClockLowTimeMs()`
 * from `types/time-control.ts`. Kept for the legacy GameClock/NetworkGameClock
 * components, which are no longer rendered by the app.
 */
export const CLOCK_LOW_TIME_MS = 20_000;

/**
 * Quanto tiene il server una partita in vita dopo che un giocatore si e'
 * scollegato, prima di assegnarla all'avversario.
 *
 * Sta qui, e non solo lato server, perche' il client ci deve stare DENTRO: ogni
 * attesa che il rientro si concede (tetti sugli ack, ritentativi, rinnovo del
 * token) e' tempo tolto a questa finestra, e la finestra scorre dal
 * SCOLLEGAMENTO, non dalla riconnessione. Con la costante condivisa quel budget
 * si calcola invece di ricordarselo.
 */
export const GRACE_PERIOD_MS = 30_000;

/**
 * Quanto sfasamento d'orologio si tollera su un access token.
 *
 * Sta qui, e non da due parti, perche' i due lati la devono avere UGUALE.
 *
 * Il client considerava un token buono fino a 60 s dopo `exp` — per non
 * buttarne via uno valido quando l'orologio del telefono e' avanti — mentre il
 * server verificava con tolleranza ZERO. In quei 60 secondi il client
 * consegnava all'handshake un token che il server rifiutava, e la connessione
 * nasceva ANONIMA senza che nessuno provasse a rinnovare: il client era
 * convinto di avere un token buono. Con un orologio client indietro la finestra
 * si allargava ancora.
 *
 * Erano due numeri in due file di due pacchetti diversi. Adesso e' uno.
 *
 * Sessanta secondi su un token da quindici minuti sono lo sfasamento ordinario
 * di un telefono, non un allungamento della sessione.
 */
export const TOLLERANZA_OROLOGIO_MS = 60_000;
